import React, { useState } from 'react'
import { Link, NavLink, useNavigate } from 'react-router-dom'
import { SearchBar } from '../search/SearchBar'
import { ThemeToggle } from '../ui/ThemeToggle'
import { LoginButton } from '../auth/LoginButton'
import { UserAvatar } from '../auth/UserAvatar'
import { useAuth } from '../../hooks/useAuth'

export const Header: React.FC = () => {
  const [menuOpen, setMenuOpen] = useState(false)
  const navigate = useNavigate()
  const { isLoggedIn } = useAuth()

  const handleSearch = (q: string) => {
    if (q.trim()) {
      navigate(`/search?q=${encodeURIComponent(q.trim())}`)
    }
  }

  return (
    <>
      <style>{`
        .header {
          position: sticky;
          top: 0;
          z-index: 100;
          background-color: var(--bg-secondary);
          border-bottom: 0.5px solid var(--border-color-solid);
          height: 60px;
          display: flex;
          align-items: center;
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
        }
        .header-inner {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          max-width: 1100px;
          margin: 0 auto;
          padding: 0 20px;
          gap: 16px;
        }
        .header-logo {
          text-decoration: none;
          color: var(--accent-green);
          font-family: 'JetBrains Mono', 'Courier New', monospace;
          font-size: 0.95rem;
          font-weight: 700;
          white-space: nowrap;
          letter-spacing: -0.01em;
          flex-shrink: 0;
        }
        .header-logo:hover {
          color: var(--accent-green);
          text-decoration: none;
          opacity: 0.8;
        }
        .header-nav {
          display: flex;
          align-items: center;
          gap: 2px;
        }
        .header-nav-link {
          color: var(--text-secondary);
          text-decoration: none;
          font-family: 'JetBrains Mono', 'Courier New', monospace;
          font-size: 0.8rem;
          padding: 5px 10px;
          border-radius: var(--radius-sm, 4px);
          transition: color 0.15s ease, background-color 0.15s ease;
          white-space: nowrap;
        }
        .header-nav-link:hover {
          color: var(--accent-blue);
          text-decoration: none;
          background-color: var(--bg-tertiary);
        }
        .header-nav-link.active {
          color: var(--accent-blue);
          background-color: rgba(133, 183, 235, 0.08);
        }
        .header-right {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-shrink: 0;
        }
        .header-search-wrap {
          width: 200px;
        }
        .hamburger-btn {
          display: none;
          background: none;
          border: 0.5px solid var(--border-color-solid);
          color: var(--text-secondary);
          font-family: 'JetBrains Mono', 'Courier New', monospace;
          font-size: 0.82rem;
          padding: 5px 10px;
          border-radius: var(--radius-sm, 4px);
          cursor: pointer;
          flex-shrink: 0;
        }
        .hamburger-btn:hover {
          border-color: var(--accent-blue);
          color: var(--accent-blue);
        }
        .mobile-menu {
          display: none;
          position: absolute;
          top: 60px;
          left: 0;
          right: 0;
          background-color: var(--bg-secondary);
          border-bottom: 0.5px solid var(--border-color-solid);
          padding: 12px 20px;
          flex-direction: column;
          gap: 8px;
          z-index: 99;
        }
        .mobile-menu.open {
          display: flex;
        }
        .mobile-menu-link {
          color: var(--text-secondary);
          text-decoration: none;
          font-family: 'JetBrains Mono', 'Courier New', monospace;
          font-size: 0.875rem;
          padding: 8px 0;
          border-bottom: 0.5px solid var(--border-color-solid);
          transition: color 0.15s ease;
        }
        .mobile-menu-link:last-of-type {
          border-bottom: none;
        }
        .mobile-menu-link:hover {
          color: var(--accent-blue);
          text-decoration: none;
        }
        .mobile-menu-search {
          padding: 8px 0;
        }
        @media (max-width: 700px) {
          .header-nav { display: none; }
          .header-search-wrap { display: none; }
          .hamburger-btn { display: block; }
        }
        @media (min-width: 701px) {
          .mobile-menu { display: none !important; }
        }
      `}</style>
      <header className="header">
        <div className="header-inner">
          <Link to="/" className="header-logo" aria-label="CodeToByte home">
            &lt;/&gt; CodeToByte
          </Link>

          <nav className="header-nav" aria-label="Main navigation">
            <NavLink
              to="/"
              end
              className={({ isActive }) =>
                `header-nav-link${isActive ? ' active' : ''}`
              }
            >
              ./home
            </NavLink>
            <NavLink
              to="/search"
              className={({ isActive }) =>
                `header-nav-link${isActive ? ' active' : ''}`
              }
            >
              ./search
            </NavLink>
            <NavLink
              to="/about"
              className={({ isActive }) =>
                `header-nav-link${isActive ? ' active' : ''}`
              }
            >
              ./about
            </NavLink>
          </nav>

          <div className="header-right">
            <div className="header-search-wrap">
              <SearchBar onSearch={handleSearch} placeholder="search..." />
            </div>
            <ThemeToggle />
            {isLoggedIn ? <UserAvatar compact /> : <LoginButton compact />}
          </div>

          <button
            className="hamburger-btn"
            onClick={() => setMenuOpen((o) => !o)}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
          >
            {menuOpen ? '[x]' : '[=]'}
          </button>
        </div>

        {/* Mobile dropdown */}
        <nav
          className={`mobile-menu${menuOpen ? ' open' : ''}`}
          aria-label="Mobile navigation"
        >
          <Link
            to="/"
            className="mobile-menu-link"
            onClick={() => setMenuOpen(false)}
          >
            ./home
          </Link>
          <Link
            to="/search"
            className="mobile-menu-link"
            onClick={() => setMenuOpen(false)}
          >
            ./search
          </Link>
          <Link
            to="/about"
            className="mobile-menu-link"
            onClick={() => setMenuOpen(false)}
          >
            ./about
          </Link>
          <div className="mobile-menu-search">
            <SearchBar onSearch={(q) => { handleSearch(q); setMenuOpen(false) }} placeholder="search..." />
          </div>
        </nav>
      </header>
    </>
  )
}
